import { Button } from 'antd';
import { doc } from 'firebase/firestore';
import React, { useContext } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';

import FacebookIcon from '../../components/UI/Icon-SVG/FacebookIcon';
import LinkedinIcon from '../../components/UI/Icon-SVG/LinkedIn';
import ProfileIcon from '../../components/UI/Icon-SVG/ProfileIcon';
import { Context } from '../../index';
import useCreatePrivateChat from '../Chat/hooks/useCreatePrivateChat';

const Container = styled.div`
  width: 100%;
  background: #fff;
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: auto;
  padding-bottom: 24px;

  .article {
    font-family: Open Sans, sans-serif;
    font-size: 18px;
    font-weight: bold;
    line-height: 27px;
    margin: 0;
  }

  .article-shadow {
    font-family: Open Sans, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #a6b0cf;
    margin-top: 6px;
  }
`;

const View = styled.div`
  width: 40%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  max-width: 520px;
`;

const AvatarBlock = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #c6dbff;
  position: relative;
  margin-top: 10px;

  .avatar__none {
    position: absolute;
    top: 26%;
    left: 25%;
  }

  img {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background-size: cover;
  }
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  margin-top: 15px;

  svg path {
    fill: #397ef3;
  }
`;

const FacebookIconLink = styled.a`
  margin-left: 50px;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
`;
const LinkedIconLink = styled.a`
  margin-left: 17px;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
`;

const Text = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  margin: 0;
`;

const ShadowText = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  margin: 0;
  color: #a6b0cf;
`;

const ProfileItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  margin: 0;
  color: #a6b0cf;
  margin-bottom: 6px;
`;

const LocationWrapper = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: row;
`;

const EmailWrapper = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: row;
`;

const Library = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 24px;
  margin-bottom: 32px;
`;

const Photo = styled.img`
  width: 88px;
  height: 88px;
  border-radius: 5px;
  margin-right: 18px;
  background: #a6b0cf;
  margin-bottom: 18px;
`;

const PublicationsItem = styled.a`
  color: #397ef3;
  font-size: 16px;
  margin-top: 12px;
  font-family: 'Open Sans', sans-serif;
  display: block;
`;

const DirectMessage = styled(Button)`
  max-width: 200px;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const EditProfileBtn = styled(Button)`
  max-width: 200px;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const ProfileLawyer = ({ isMyProfile = true }) => {
  const { auth, firestore } = useContext(Context);
  const [user] = useAuthState(auth);
  const { id } = useParams();
  const userId = isMyProfile ? user.uid : id;
  const history = useHistory();

  const [userData] = useDocumentData(doc(firestore, 'users', userId));
  const [lawyerFullProfileData] = useDocumentData(doc(firestore, 'lawyer_full_profile', userId));
  const { creatPrivateChat } = useCreatePrivateChat();
  const goToEditProfile = () => {
    history.push('/edit-profile');
  };

  const name =
    userData?.first_name && userData?.last_name
      ? `${userData?.first_name} ${userData?.last_name}`
      : '-';
  const {
    avatar,
    career,
    education,
    email,
    facebook,
    free_consultation,
    hourly_rate,
    linkedin,
    office_address,
    office_phone,
    office_site,
    photos,
    practice_area,
    user_location,
    publications,
  } = lawyerFullProfileData ?? {};

  return (
    <Container>
      <View>
        <p className="article">General information</p>
        <AvatarBlock>
          {!avatar?.avatar_path ? (
            <div className="avatar__none">
              <ProfileIcon color="#397EF3" />
            </div>
          ) : (
            <img src={avatar.avatar_path} alt="Avatar" />
          )}
        </AvatarBlock>
        <NameWrapper>
          <Text>{name}</Text>
          {facebook && (
            <FacebookIconLink href={facebook}>
              <FacebookIcon />
            </FacebookIconLink>
          )}
          {linkedin && (
            <LinkedIconLink href={linkedin}>
              <LinkedinIcon />
            </LinkedIconLink>
          )}
        </NameWrapper>
        {!isMyProfile && (
          <DirectMessage onClick={() => creatPrivateChat(user.uid, id)}>
            Direct Messages
          </DirectMessage>
        )}
        {isMyProfile && <EditProfileBtn onClick={goToEditProfile}>Edit Profile</EditProfileBtn>}
        <ProfileItem style={{ marginTop: 16 }}>
          <Label>Sphere of law</Label>
          <Text>{practice_area || '-'}</Text>
        </ProfileItem>
        <LocationWrapper>
          <ProfileItem style={{ marginRight: 100 }}>
            <Label>Location</Label>
            <Text>{user_location || '-'}</Text>
          </ProfileItem>
          <ProfileItem>
            <Label>Free consultation</Label>
            <Text>{free_consultation ? 'Yes' : 'No'}</Text>
          </ProfileItem>
        </LocationWrapper>
        <ProfileItem style={{ marginTop: 16, marginBottom: 24 }}>
          <Label>Hourly payment</Label>
          <Text>{hourly_rate || '-'}</Text>
        </ProfileItem>
        <p className="article">Contacts</p>
        <ProfileItem style={{ marginTop: 16 }}>
          <Label>Address</Label>
          <Text>{office_address || '-'}</Text>
        </ProfileItem>
        <ProfileItem style={{ marginTop: 16 }}>
          <Label>Office phone number</Label>
          <Text>{office_phone || '-'}</Text>
        </ProfileItem>
        <EmailWrapper>
          <ProfileItem style={{ marginRight: 100 }}>
            <Label>Email</Label>
            <Text>{email || '-'}</Text>
          </ProfileItem>
          <ProfileItem>
            <Label>Website</Label>
            <Text>{office_site || '-'}</Text>
          </ProfileItem>
        </EmailWrapper>
      </View>
      <View style={{ marginLeft: 120 }}>
        {!!photos?.length && (
          <>
            <p className="article">Photos</p>
            <ShadowText>Everything about me: certificates, diplomas, etc.</ShadowText>
            <Library>
              {photos.map((item, key) => (
                <Photo key={key} src={item.avatar_path} />
              ))}
            </Library>
          </>
        )}
        {!!career?.length && (
          <>
            <p className="article">Сareer</p>
            {career.map((item, key) => (
              <ProfileItem key={key} style={{ marginTop: 16 }}>
                <Text>{item.career_name}</Text>
                <ShadowText>
                  {item.career_start} - {item.career_end === '-1' ? 'now' : item.career_end}
                </ShadowText>
              </ProfileItem>
            ))}
          </>
        )}
        {!!education?.length && (
          <>
            <p className="article" style={{ marginTop: 24 }}>
              Education
            </p>
            {education.map((item, key) => (
              <ProfileItem key={key} style={{ marginTop: 16 }}>
                <Text>{item.education_name}</Text>
                <ShadowText>{item.education_degree}</ShadowText>
              </ProfileItem>
            ))}
          </>
        )}
        {!!publications?.length && (
          <>
            <p className="article" style={{ marginTop: 24 }}>
              Publications
            </p>
            {publications.map((item, key) => (
              <PublicationsItem key={key} href={item.publication_url}>
                {item.publication_name}
              </PublicationsItem>
            ))}
          </>
        )}
      </View>
    </Container>
  );
};

export default ProfileLawyer;
